import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import PageWrapper from '../components/PageWrapper';
import ContentModal from '../components/ContentModal';
import { ExperimentCardSkeleton } from '../components/SkeletonLoader';
import api from '../services/api';
import SafeImage from '../components/SafeImage';
import '../styles/pages/ExperimentationZonePage.css';

const ExperimentationZonePage = () => {
  const location = useLocation();
  const [selectedStatus, setSelectedStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [experiments, setExperiments] = useState([]);
  const [selectedExperiment, setSelectedExperiment] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const statuses = ['Active', 'Testing', 'Completed', 'Planned'];

  useEffect(() => {
    fetchExperiments();
  }, [selectedStatus]);

  const fetchExperiments = async () => {
    try {
      setLoading(true);
      
      const params = {};
      if (selectedStatus) {
        params.status = selectedStatus;
      }
      
      const response = await api.getExperiments(params);

      if (response.success && response.data) {
        setExperiments(response.data);
      } else {
        setExperiments([]);
      }
    } catch (error) {
      console.error('Error fetching experiments:', error);
      setExperiments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = (status) => {
    setSelectedStatus(status);
  };

  const openModal = (experiment) => {
    setSelectedExperiment(experiment);
    setIsModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedExperiment(null);
    document.body.style.overflow = 'unset';
  };

  const truncateText = (text, maxLength) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim();
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.5 } }
  };

  return (
    <PageWrapper>
      <SEO
        title="Experimentation Zone | Supernova Lab"
        description="The LabZone is Barbados' proving ground - a place to run real-world experiments where regulation is rigid, undefined, or yet to exist"
        keywords="experiments, innovation, testing, lab zone, Barbados"
      />

      <main className="experimentation-zone-page">
        <motion.section
          className="zone-header-section"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="container">
            <div className="zone-badge">Test</div>
            <h1 className="zone-main-title">
              <span className="animated-title-primary">Experimentation</span>
              <br />
              <span className="text-gradient-dark">Zone</span>
            </h1>

            <h2 className="zone-subtitle">Where the rules are tested</h2>

            <div className="zone-description">
              <p>
                The LabZone is Barbados' proving ground a place to run real-world experiments where regulation is rigid, undefined, or yet to exist.<br /><br />
                Here, mission-driven solutions are exposed to national systems, stress-tested under public oversight, and shaped into evidence
                governments can act on.
              </p>
            </div>
          </div>
        </motion.section>

        <section className="featured-experiments-section">
          <div className="container">
            <motion.div
              className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
            >
              <h2 className="featured-text-title mb-0">Featured Experiments</h2>
              
              <select
                className="form-select whats-next-filter-btn"
                value={selectedStatus}
                onChange={(e) => handleStatusChange(e.target.value)}
                style={{ width: '200px', cursor: 'pointer' }}
              >
                <option value="">All Status</option>
                {statuses.map(status => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </motion.div>

            <motion.div
              className="row g-4"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              key={`${loading}-${selectedStatus}`}
            >
              {loading ? (
                Array(6).fill(0).map((_, index) => (
                  <div key={index} className="col-lg-4 col-md-6">
                    <ExperimentCardSkeleton />
                  </div>
                ))
              ) : experiments.length > 0 ? (
                experiments.map((experiment) => (
                  <motion.div key={experiment.id} className="col-lg-4 col-md-6" variants={itemVariants}>
                    <div className="experiment-card">
                      <div className="experiment-image-wrapper">
                        <SafeImage
                          src={experiment.image}
                          alt={experiment.title}
                          className="experiment-image"
                          placeholderText={experiment.title}
                        />
                        <span
                          className="experiment-status-badge"
                          style={{ backgroundColor: experiment.status_color }}
                        >
                          {experiment.status}
                        </span>
                      </div>
                      <div className="experiment-card-content">
                        <h3 className="experiment-title">{experiment.title}</h3>
                        <p className="experiment-description">
                          {experiment.description.length > 120 
                            ? truncateText(experiment.description, 120)
                            : experiment.description
                          }
                          {experiment.description.length > 120 && (
                            <>
                              ...{' '}
                              <span 
                                className="read-more-text"
                                onClick={() => openModal(experiment)}
                              >
                                Read More
                              </span>
                            </>
                          )}
                        </p>
                        <p className="experiment-metric">{experiment.metric}</p>
                      </div>
                    </div>
                  </motion.div>
                ))
              ) : (
                <div className="col-12 text-center py-5">
                  <p className="text-muted">
                    No experiments available {selectedStatus && `with status "${selectedStatus}"`}
                  </p>
                </div>
              )}
            </motion.div>
          </div>
        </section>
      </main>

      {/* Content Modal */}
      <ContentModal
        isOpen={isModalOpen}
        onClose={closeModal}
        content={selectedExperiment}
        type="experiment"
      />
    </PageWrapper>
  );
};

export default ExperimentationZonePage;